import { useState } from 'react';
import { useCreateProject, useSyncProjectCollaborators, useSearchProfiles, solutionKeyToInt, useProjects } from '@/hooks/useProjects';
import { useGenerateProjectSuggestions } from '@/hooks/useProjectSuggestions';
import { useSelectedCohort } from '@/hooks/useSelectedCohort';
import { useAuth } from '@/contexts/AuthContext';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { Badge } from '@/components/ui/badge';
import { Plus, Save, X, UserPlus, FileText, Code, Bot, Zap, BarChart3, Search, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const SOLUTION_TYPES = [
  { id: 'content', label: 'Creación de Contenido con IA', icon: FileText, color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  { id: 'vibecoding', label: 'Vibecoding y Creación de Apps con IA', icon: Code, color: 'bg-purple-500/10 text-purple-600 border-purple-500/20' },
  { id: 'assistant', label: 'Asistente/ Agente IA', icon: Bot, color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  { id: 'automation', label: 'Automatización IA', icon: Zap, color: 'bg-orange-500/10 text-orange-600 border-orange-500/20' },
  { id: 'data', label: 'Análisis de Datos con IA', icon: BarChart3, color: 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20' },
];

interface InvitedUser {
  id: string;
  name: string;
}

interface ProjectFormData {
  title: string;
  solutionType: string;
  problem: string;
  targetAudience: string;
  dataUsed: string;
  invitedUsers: InvitedUser[];
  cohortId: string;
}

interface NewProjectDialogProps {
  trigger?: React.ReactNode;
  onProjectCreated?: (project: ProjectFormData) => void;
}

export function NewProjectDialog({ trigger, onProjectCreated }: NewProjectDialogProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const createProject = useCreateProject();
  const generateSuggestions = useGenerateProjectSuggestions();
  const syncCollaborators = useSyncProjectCollaborators();
  const { enrollments, selectedCohortId } = useSelectedCohort();
  const { hasPaidAccess } = useUserAccess();
  const { data: existingProjects = [] } = useProjects();
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const { data: searchedProfiles = [] } = useSearchProfiles(userSearchQuery);

  // Fetch max free projects limit
  const { data: maxFreeProjects = 1 } = useQuery({
    queryKey: ['platform-setting', 'max_free_projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'max_free_projects')
        .maybeSingle();
      if (error) throw error;
      return (data?.value as number) ?? 1;
    },
  });

  const isAtProjectLimit = !hasPaidAccess && existingProjects.length >= maxFreeProjects;
  
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    solutionType: '',
    problem: '',
    targetAudience: '',
    dataUsed: '',
    invitedUsers: [],
    cohortId: 'none',
  });

  const handleChange = (field: keyof ProjectFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddUser = (user: { id: string; name: string }) => {
    if (!formData.invitedUsers.find(u => u.id === user.id)) {
      setFormData(prev => ({
        ...prev,
        invitedUsers: [...prev.invitedUsers, { id: user.id, name: user.name }]
      }));
    }
    setUserSearchQuery('');
    setShowUserSearch(false);
  };

  const handleRemoveUser = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      invitedUsers: prev.invitedUsers.filter(u => u.id !== userId)
    }));
  };

  const filteredUsers = searchedProfiles
    .filter(p => !formData.invitedUsers.find(u => u.id === p.id))
    .map(p => ({ id: p.id, name: p.full_name || '', email: p.email || '' }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check project limit for free users
    if (isAtProjectLimit) {
      toast({
        title: "Límite de proyectos alcanzado",
        description: `Tu plan gratuito permite un máximo de ${maxFreeProjects} proyecto(s). Actualiza tu suscripción para crear más proyectos.`,
        variant: "destructive",
      });
      return;
    }
    
    const requiredFields: (keyof ProjectFormData)[] = [
      'title', 'solutionType', 'problem', 'targetAudience', 'dataUsed'
    ];
    
    const emptyFields = requiredFields.filter(field => !formData[field]);
    
    if (emptyFields.length > 0) {
      toast({
        title: "Campos requeridos",
        description: "Por favor completa todos los campos marcados con *",
        variant: "destructive",
      });
      return;
    }

    try {
      const newProject = await createProject.mutateAsync({
        name: formData.title,
        problem: formData.problem,
        target_audience: formData.targetAudience,
        data_used: formData.dataUsed,
        solution_type: solutionKeyToInt(formData.solutionType),
        cohort_id: formData.cohortId === 'none' ? undefined : formData.cohortId || undefined,
      });

      // Sync collaborators if any were added
      if (formData.invitedUsers.length > 0) {
        await syncCollaborators.mutateAsync({
          projectId: newProject.id,
          userIds: formData.invitedUsers.map(u => u.id),
        });
      }

      // Trigger AI suggestions generation in background
      generateSuggestions.mutate({ projectId: newProject.id, force: false });

      toast({
        title: "Proyecto creado",
        description: "El proyecto se ha creado correctamente",
      });
      
      onProjectCreated?.(formData);
      setOpen(false);
      
      setFormData({
        title: '',
        solutionType: '',
        problem: '',
        targetAudience: '',
        dataUsed: '',
        invitedUsers: [],
        cohortId: 'none',
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "No se pudo crear el proyecto",
        variant: "destructive",
      });
    }
  };

  const selectedSolutionType = SOLUTION_TYPES.find(t => t.id === formData.solutionType);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="gradient-primary gap-2">
            <Plus className="w-4 h-4" />
            Nuevo Proyecto
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle className="text-xl font-display">Nuevo Proyecto</DialogTitle>
        </DialogHeader>

        {isAtProjectLimit && (
          <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/30">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-destructive">Límite de proyectos alcanzado</p>
              <p className="text-xs text-muted-foreground mt-1">
                Tu plan gratuito permite un máximo de {maxFreeProjects} proyecto(s). Actualiza tu suscripción para crear más proyectos.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          {/* Cohort-Program Selector */}
          {enrollments.length > 0 && (
            <div className="space-y-2">
              <Label>Cohorte - Ruta</Label>
              <SearchableCombobox
                value={formData.cohortId}
                onSelect={(value) => handleChange('cohortId', value)}
                placeholder="Selecciona la cohorte"
                searchPlaceholder="Buscar cohorte o ruta..."
                options={[
                  { value: 'none', label: 'Proyecto libre (sin cohorte)', keywords: ['libre','sin','none'] },
                  ...enrollments.filter((e) => !e.view_only).map((e) => ({
                    value: e.cohort_id,
                    label: `Ruta: ${e.program_name} — ${e.cohort_name}`,
                    keywords: [e.program_name, e.cohort_name],
                  })),
                ]}
              />
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título del Proyecto *</Label>
            <Input
              id="title"
              placeholder="Ej: Asistente de Atención al Cliente"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          {/* Solution Type */}
          <div className="space-y-2">
            <Label>Tipo de Solución *</Label>
            <Select
              value={formData.solutionType}
              onValueChange={(value) => handleChange('solutionType', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo de solución" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-border z-50">
                {SOLUTION_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{type.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {selectedSolutionType && (
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${selectedSolutionType.color}`}>
                <selectedSolutionType.icon className="w-4 h-4" />
                {selectedSolutionType.label}
              </div>
            )}
          </div>

          {/* Problem */}
          <div className="space-y-2">
            <Label htmlFor="problem">Problema *</Label>
            <Textarea
              id="problem"
              placeholder="Describe el problema de negocio que quieres resolver con este proyecto..."
              value={formData.problem}
              onChange={(e) => handleChange('problem', e.target.value)}
              rows={3}
            />
          </div>

          {/* Target Audience */}
          <div className="space-y-2">
            <Label htmlFor="targetAudience">Público Objetivo *</Label>
            <Input
              id="targetAudience"
              placeholder="Ej: Clientes del área de soporte, equipo comercial, etc."
              value={formData.targetAudience}
              onChange={(e) => handleChange('targetAudience', e.target.value)}
            />
          </div>

          {/* Data Used */}
          <div className="space-y-2">
            <Label htmlFor="dataUsed">Datos Usados *</Label>
            <Textarea
              id="dataUsed"
              placeholder="¿Qué datos tienes disponibles para el proyecto? Ej: CRM, hojas de cálculo, bases de datos..."
              value={formData.dataUsed}
              onChange={(e) => handleChange('dataUsed', e.target.value)}
              rows={2}
            />
          </div>

          {/* Invited Users */}
          <div className="space-y-3">
            <Label>Usuarios Invitados</Label>
            
            {/* Selected users */}
            {formData.invitedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.invitedUsers.map((user) => (
                  <Badge 
                    key={user.id} 
                    variant="secondary"
                    className="gap-1.5 pr-1.5"
                  >
                    <span>{user.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(user.id)}
                      className="hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Add user button/search */}
            {showUserSearch ? (
              <div className="space-y-2">
                <div className="relative">
                  <Input
                    placeholder="Buscar usuarios por nombre o email..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    autoFocus
                  />
                </div>
                {userSearchQuery && filteredUsers.length > 0 && (
                  <div className="border border-border rounded-lg bg-card max-h-40 overflow-y-auto">
                    {filteredUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => handleAddUser(user)}
                        className="w-full px-3 py-2 text-left hover:bg-muted transition-colors flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xs font-bold text-white">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{user.name}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {userSearchQuery && filteredUsers.length === 0 && (
                  <p className="text-sm text-muted-foreground py-2">No se encontraron usuarios</p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUserSearch(false);
                    setUserSearchQuery('');
                  }}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setShowUserSearch(true)}
              >
                <UserPlus className="w-4 h-4" />
                Invitar colaborador
              </Button>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="gap-2">
              <Save className="w-4 h-4" />
              Crear Proyecto
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
